import numberUtils from '../utils/numberUtils';
import type { GetMultifamilyFeesDictionaryItemsDto } from '@zg-rentals/ts-rental-manager-api-client';
type ConstraintsProps = {
  [key: string]: unknown;
};

const costsAndFeesConstraints: {
  values?: ConstraintsProps;
  getcostsAndFeesConstraints: (dictionaryItems: GetMultifamilyFeesDictionaryItemsDto) => void;
} = {
  values: undefined,
  getcostsAndFeesConstraints(dictionaryItems: GetMultifamilyFeesDictionaryItemsDto) {
    if (this.values) {
      return;
    }

    const { STEPPED, MULTIPLIER_OF_RENT, PERCENTAGE_OF_RENT } = dictionaryItems.feeCalculationType;
    const FLAT_FEE = dictionaryItems.feeCalculationType.FLAT_FEE!;
    const PER_ITEM_FIXED = dictionaryItems.feeCalculationType.PER_ITEM_FIXED!;
    const RANGE = dictionaryItems.feeCalculationType.RANGE!;

    this.values = {
      feeCategory: {
        presence: {
          allowEmpty: false,
          message: 'Make a selection to continue',
        },
      },
      feeName: {
        presence: {
          allowEmpty: false,
          message: 'Make a selection to continue',
        },
      },
      feePaymentFrequencyType: {
        presence: {
          allowEmpty: false,
          message: 'Make a selection to continue',
        },
      },
      feeFormat: {
        presence: {
          allowEmpty: false,
          message: 'Make a selection to continue',
        },
      },
      customFeeName: {
        presence: {
          allowEmpty: false,
          message: 'Custom fee name cannot be empty',
        },
      },
      feeAmountMinFlat: {
        presence: {
          allowEmpty: false,
          message: 'Amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: FLAT_FEE.minValue,
          lessThanOrEqualTo: FLAT_FEE.maxValue,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(FLAT_FEE.minValue)} and ${numberUtils.asCurrency(FLAT_FEE.maxValue)}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(FLAT_FEE.minValue)} and ${numberUtils.asCurrency(FLAT_FEE.maxValue)}`,
        },
      },
      feeAmountMin: {
        presence: {
          allowEmpty: false,
          message: 'Amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: PER_ITEM_FIXED.minValue,
          lessThanOrEqualTo: PER_ITEM_FIXED.maxValue,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(PER_ITEM_FIXED.minValue, '$0,0.00')} and ${numberUtils.asCurrency(PER_ITEM_FIXED.maxValue, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(PER_ITEM_FIXED.minValue, '$0,0.00')} and ${numberUtils.asCurrency(PER_ITEM_FIXED.maxValue, '$0,0.00')}`,
        },
      },
      feeAmountMinRange: {
        presence: {
          allowEmpty: false,
          message: 'Amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: RANGE.minValue,
          lessThanOrEqualTo: RANGE.maxValue,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between $${RANGE.minValue.toFixed(2)} and ${numberUtils.asCurrency(RANGE.maxValue, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between $${RANGE.minValue.toFixed(2)} and ${numberUtils.asCurrency(RANGE.maxValue, '$0,0.00')}`,
        },
      },
      feeAmountMax: {
        presence: {
          allowEmpty: false,
          message: 'Amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: RANGE.minValue,
          lessThanOrEqualTo: RANGE.maxValue,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(RANGE.minValue, '$0,0.00')} and ${numberUtils.asCurrency(RANGE.maxValue, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(RANGE.minValue, '$0,0.00')} and ${numberUtils.asCurrency(RANGE.maxValue, '$0,0.00')}`,
        },
      },
      steppedFeeAmounts: {
        presence: {
          allowEmpty: false,
          message: 'Stepped fee amounts cannot be blank',
        },
      },
      steppedFeeAmount1: {
        presence: {
          allowEmpty: false,
          message: 'First item amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: STEPPED?.minValue || 0.01,
          lessThanOrEqualTo: STEPPED?.maxValue || 1000000,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
        },
      },
      steppedFeeAmount2: {
        presence: {
          allowEmpty: false,
          message: 'Second item amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: STEPPED?.minValue || 0.01,
          lessThanOrEqualTo: STEPPED?.maxValue || 1000000,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
        },
      },
      steppedFeeAmount3: {
        presence: {
          allowEmpty: false,
          message: 'Third item amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: STEPPED?.minValue || 0.01,
          lessThanOrEqualTo: STEPPED?.maxValue || 1000000,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
        },
      },
      steppedFeeAmount4: {
        presence: {
          allowEmpty: false,
          message: 'Fourth item amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: STEPPED?.minValue || 0.01,
          lessThanOrEqualTo: STEPPED?.maxValue || 1000000,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
        },
      },
      steppedFeeAmount5: {
        presence: {
          allowEmpty: false,
          message: 'Fifth item amount cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: STEPPED?.minValue || 0.01,
          lessThanOrEqualTo: STEPPED?.maxValue || 1000000,
          notValid: 'Amount must be a number',
          notGreaterThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
          notLessThanOrEqualTo: `Amount must be between ${numberUtils.asCurrency(STEPPED?.minValue || 0.01, '$0,0.00')} and ${numberUtils.asCurrency(STEPPED?.maxValue || 1000000, '$0,0.00')}`,
        },
      },
    };

    if (MULTIPLIER_OF_RENT) {
      this.values.feeAmountMultiple = {
        presence: {
          allowEmpty: false,
          message: 'Multiple cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: MULTIPLIER_OF_RENT.minValue,
          lessThanOrEqualTo: MULTIPLIER_OF_RENT.maxValue,
          notValid: 'Multiple must be a number',
          notGreaterThanOrEqualTo: `Enter a multiple up to ${MULTIPLIER_OF_RENT.maxValue}, with a maximum of two decimal places`,
          notLessThanOrEqualTo: `Multiplier must not exceed ${MULTIPLIER_OF_RENT.maxValue}`,
        },
      };
    }

    if (PERCENTAGE_OF_RENT) {
      this.values.feeAmountPercent = {
        presence: {
          allowEmpty: false,
          message: 'Percent cannot be blank',
        },
        numericality: {
          greaterThanOrEqualTo: PERCENTAGE_OF_RENT.minValue,
          lessThanOrEqualTo: PERCENTAGE_OF_RENT.maxValue,
          notValid: 'Percent must be a number',
          notGreaterThanOrEqualTo: `Percentage must be from 1 to ${PERCENTAGE_OF_RENT.maxValue}. Use the format "Multiple of base rent" if you need a higher value.`,
          notLessThanOrEqualTo: `Percentage must be from 1 to ${PERCENTAGE_OF_RENT.maxValue}. Use the format "Multiple of base rent" if you need a higher value.`,
        },
      };
    }
  },
};

export default costsAndFeesConstraints;
